import React from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPalette,
  faClock,
  faTicketAlt,
  faCalendarAlt,
} from '@fortawesome/free-solid-svg-icons';
import Footer from '../../Footer/Footer';
import ParallaxBanner from '../../common/Parralax-banner';

import artTourImage from '../../../assets/tours/art-lovers.webp';

const PageContainer = styled.div`
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  background-color: ${props => props.theme.colors.offWhite};
  padding-top: 120px;
`;

const ContentContainer = styled.div`
  max-width: 900px;
  margin: 0 auto;
  padding: 4rem 2rem;
  width: 100%;

  @media (max-width: 768px) {
    padding: 3rem 1.5rem;
  }
`;

const SectionTitle = styled.h2`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2.5rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1.5rem;
  text-align: center;

  &:after {
    content: '';
    display: block;
    width: 60px;
    height: 3px;
    background-color: ${props => props.theme.colors.accent};
    margin: 1rem auto 0;
  }

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const IntroText = styled.p`
  font-family: ${props => props.theme.fonts.main};
  font-size: 1.2rem;
  line-height: 1.8;
  color: ${props => props.theme.colors.primary};
  max-width: 700px;
  margin: 0 auto 3rem;
  text-align: center;
`;

const TourCard = styled.div`
  background: white;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
`;

const TourImage = styled.div`
  height: 380px;
  background-image: url(${props => props.image});
  background-size: cover;
  background-position: center;

  @media (max-width: 768px) {
    height: 240px;
  }
`;

const TourContent = styled.div`
  padding: 2.5rem;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const TourTitle = styled.h3`
  font-family: ${props => props.theme.fonts.main};
  font-size: 2rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;

  svg {
    color: ${props => props.theme.colors.accent};
  }
`;

const TourDescription = styled.p`
  font-size: 1.1rem;
  line-height: 1.7;
  color: ${props => props.theme.colors.secondary};
  margin-bottom: 1.5rem;
`;

const TourDetails = styled.div`
  display: flex;
  align-items: center;
  gap: 2rem;
  margin-bottom: 1.5rem;
  color: ${props => props.theme.colors.secondary};
  font-size: 1rem;

  svg {
    color: ${props => props.theme.colors.accent};
  }

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.75rem;
  }
`;

const TourDateTime = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 0.75rem;
  color: ${props => props.theme.colors.secondary};
  font-size: 1rem;
  margin-bottom: 1.5rem;

  svg {
    color: ${props => props.theme.colors.accent};
    margin-top: 0.2rem;
    flex-shrink: 0;
  }
`;

const DateList = styled.ul`
  list-style: none;
  padding: 0;
  margin: 0.25rem 0 0;
  display: flex;
  flex-direction: column;
  gap: 0.3rem;
`;

const DateItem = styled.li`
  font-size: 0.95rem;
  color: ${props => props.theme.colors.secondary};
`;


const TalesTapestriesTour = () => {
  const dates = [
    "Friday 4th September",
    "Friday 11th September",
    "Friday 18th September",
    "Friday 25th September",
    "Friday 2nd October",
    "Friday 9th October",
    "Friday 16th October",
  ];

  return (
    <PageContainer>
      <ParallaxBanner
        title="Tales of the Tapestries"
        subtitle="Uncover the Epic Stories Woven into Belvoir Castle's Walls"
        image={artTourImage}
      />

      <ContentContainer>
        <SectionTitle>Tales of the Tapestries</SectionTitle>
        <IntroText>
          A brand new featured tour celebrating Belvoir Castle's magnificent Mortlake tapestries —
          masterpieces of 17th-century English craftsmanship, rich with drama, mythology and history.
        </IntroText>

        <TourCard>
          <TourImage image={artTourImage} />
          <TourContent>
            <TourTitle>
              <FontAwesomeIcon icon={faPalette} />
              Tales of the Tapestries
            </TourTitle>
            <TourDescription>
              As the world celebrates the long-awaited return of the Bayeux Tapestry to the UK,
              Belvoir Castle invites you to discover the extraordinary woven stories within its own
              walls. Our new featured tour, "Tales of the Tapestries," shines a spotlight on the
              Castle's magnificent Mortlake tapestries, masterpieces of 17th-century English
              craftsmanship.
            </TourDescription>
            <TourDescription>
              Woven in the renowned Mortlake workshops, these grand textiles are more than
              decoration; they are epic narratives in silk and wool, rich with drama, mythology,
              symbolism and history. Journey not just through the Castle's state rooms, but our
              private Tapestry Bedroom too, and uncover the powerful stories depicted in these vast
              woven scenes, learn how these remarkable works were commissioned, collected and, in
              some cases, gifted to Belvoir Castle, becoming part of its enduring heritage.
            </TourDescription>
            <TourDetails>
              <div><FontAwesomeIcon icon={faClock} /> 1½ hours</div>
              <div><FontAwesomeIcon icon={faTicketAlt} /> £5 per person</div>
            </TourDetails>
            <TourDateTime>
              <FontAwesomeIcon icon={faCalendarAlt} />
              <div>
                <strong>Tour dates at 2:00pm:</strong>
                <DateList>
                  {dates.map((date, i) => (
                    <DateItem key={i}>{date}</DateItem>
                  ))}
                </DateList>
              </div>
            </TourDateTime>
          </TourContent>
        </TourCard>
      </ContentContainer>

      <Footer />
    </PageContainer>
  );
};

export default TalesTapestriesTour;
